
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/base/SEOHead';

export default function IncontinenceGuidePage() {
  const relatedArticles = [
    {
      title: 'Typische Anzeichen des Alterns bei Hunden',
      excerpt: 'Erkennen Sie die ersten Zeichen des Älterwerdens.',
      link: '/ratgeber/altersanzeichen',
      image: 'https://public.readdy.ai/ai/img_res/11ac27a2ecf3b57ceff6fc0dcda2a62c.jpg'
    },
    {
      title: 'Das Zuhause seniorengerecht gestalten',
      excerpt: 'Praktische Anpassungen für mehr Sicherheit.',
      link: '/ratgeber/zuhause-seniorengerecht',
      image: 'https://readdy.ai/api/search-image?query=Cozy%20home%20interior%20prepared%20for%20senior%20dog%20with%20orthopedic%20bed%20non%20slip%20rugs%20easy%20access%20water%20bowl%20warm%20lighting%20comfortable%20safe%20environment%20earth%20tones%20minimalist%20style&width=400&height=250&seq=home-adapt-1&orientation=landscape'
    },
    {
      title: 'Ab wann gilt ein Hund als Senior?',
      excerpt: 'Wann beginnt das Seniorenalter bei Hunden?',
      link: '/ratgeber/ab-wann-senior',
      image: 'https://readdy.ai/api/search-image?query=Dignified%20senior%20labrador%20dog%20with%20distinguished%20gray%20face%20sitting%20proudly%20soft%20natural%20lighting%20warm%20background%20showing%20wisdom%20and%20age%20gracefully%20earth%20tones%20portrait%20style&width=400&height=250&seq=senior-age-1&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-sand">
      <SEOHead 
        title="Inkontinenz beim alten Hund – Ursachen & Lösungen | Hund im Alter"
        description="Inkontinenz beim Seniorhund: Ursachen verstehen, würdevolles Management und praktische Hilfsmittel für den Alltag."
        keywords="Inkontinenz Hund, alter Hund Urin, Blasenschwäche Hund, Hund verliert Urin"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <div className="relative h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Senior%20dog%20resting%20comfortably%20on%20washable%20waterproof%20bed%20pad%20in%20cozy%20home%20setting%20warm%20natural%20lighting%20dignified%20elderly%20dog%20care%20soft%20earth%20tones%20beige%20and%20cream%20colors%20peaceful%20atmosphere%20clean%20hygienic%20environment&width=1920&height=500&seq=incontinence-hero-1&orientation=landscape"
            alt="Inkontinenz beim alten Hund"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-16 w-full">
              <nav className="text-sm text-white/80 mb-4">
                <Link to="/" className="hover:text-white">Start</Link>
                <span className="mx-2">/</span>
                <Link to="/ratgeber" className="hover:text-white">Ratgeber</Link>
                <span className="mx-2">/</span>
                <span>Inkontinenz</span>
              </nav>
              <span className="inline-block px-3 py-1 bg-teal text-white text-sm font-medium rounded-full mb-4">
                Gesundheit & Pflege
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Inkontinenz beim alten Hund: Was Sie wissen sollten
              </h1>
              <p className="text-xl text-white/90 leading-relaxed max-w-2xl">
                Ursachen, Management-Strategien und würdevolle Lösungen für altersbedingte Inkontinenz bei Ihrem Seniorhund.
              </p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          {/* Author & Date */}
          <div className="flex items-center gap-4 mb-12 pb-8 border-b border-gray-200">
            <div className="w-12 h-12 rounded-full bg-teal/20 flex items-center justify-center">
              <i className="ri-user-heart-line text-teal text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-navy">Hund-im-Alter Redaktion</p>
              <p className="text-sm text-gray-500">Aktualisiert: Januar 2025 · 10 Min. Lesezeit</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Wenn Ihr älterer Hund plötzlich Urin verliert oder nachts sein Körbchen nass macht, kann das für beide Seiten belastend sein. Doch Inkontinenz im Alter ist keine Seltenheit und vor allem: <strong>kein Grund zur Scham</strong>. Mit dem richtigen Verständnis und praktischen Lösungen können Sie Ihrem Seniorhund ein würdevolles Leben ermöglichen.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 mb-12">
            <h2 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
              <i className="ri-list-check text-teal"></i>
              Inhalt dieses Artikels
            </h2>
            <ul className="space-y-2">
              <li>
                <a href="#was-ist-inkontinenz" className="text-teal hover:underline cursor-pointer">1. Was ist Altersinkontinenz?</a>
              </li>
              <li>
                <a href="#ursachen" className="text-teal hover:underline cursor-pointer">2. Häufige Ursachen bei Seniorhunden</a>
              </li>
              <li>
                <a href="#anzeichen" className="text-teal hover:underline cursor-pointer">3. Typische Anzeichen erkennen</a>
              </li>
              <li>
                <a href="#tierarzt" className="text-teal hover:underline cursor-pointer">4. Wann zum Tierarzt?</a>
              </li>
              <li>
                <a href="#management" className="text-teal hover:underline cursor-pointer">5. Praktisches Management im Alltag</a>
              </li>
              <li>
                <a href="#hilfsmittel" className="text-teal hover:underline cursor-pointer">6. Hilfreiche Produkte und Hilfsmittel</a>
              </li>
              <li>
                <a href="#wuerde" className="text-teal hover:underline cursor-pointer">7. Die Würde Ihres Hundes bewahren</a>
              </li>
            </ul>
          </div>

          {/* Section 1 */}
          <section id="was-ist-inkontinenz" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              1. Was ist Altersinkontinenz?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Inkontinenz bezeichnet den unwillkürlichen Verlust von Urin – Ihr Hund kann also nicht mehr kontrollieren, wann und wo er Wasser lässt. Im Gegensatz zu Unsauberkeit aus Verhaltensgründen passiert dies <strong>ohne Absicht</strong> und oft, ohne dass der Hund es selbst bemerkt.
            </p>
            <div className="bg-teal/10 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-teal text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-navy mb-2">Wichtig zu verstehen</h4>
                  <p className="text-gray-700">
                    Inkontinenz ist <strong>keine Verhaltensstörung</strong> und kein Zeichen von Ungehorsam. Ihr Hund macht das nicht absichtlich und sollte niemals dafür bestraft werden.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              Bei älteren Hunden ist Inkontinenz relativ häufig. Studien zeigen, dass etwa 20% aller Hunde über 10 Jahre von irgendeiner Form der Blasenschwäche betroffen sind. Bei kastrierten Hündinnen liegt die Rate sogar noch höher.
            </p>
          </section>

          {/* Section 2 */}
          <section id="ursachen" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              2. Häufige Ursachen bei Seniorhunden
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Die Ursachen für Inkontinenz im Alter sind vielfältig. Hier die häufigsten:
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-pulse-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Schwäche des Schließmuskels</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Mit dem Alter lässt die Muskelkraft nach – auch beim Blasenschließmuskel. Besonders im Schlaf, wenn die Muskeln entspannt sind, kann Urin austreten. Dies ist die häufigste Ursache bei älteren Hunden.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-flask-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Hormonelle Veränderungen</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Bei kastrierten Hündinnen kann ein Östrogenmangel die Blasenkontrolle beeinträchtigen. Dies wird als "Kastrationsinkontinenz" bezeichnet und betrifft bis zu 20% aller kastrierten Hündinnen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-brain-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Kognitive Veränderungen</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Ähnlich wie bei Demenz beim Menschen können ältere Hunde "vergessen", dass sie nach draußen müssen, oder die Signale ihrer Blase nicht mehr richtig interpretieren.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-walk-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Mobilitätsprobleme</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Wenn Ihr Hund aufgrund von Arthrose oder anderen Gelenkproblemen nicht mehr schnell genug aufstehen und nach draußen gehen kann, kann es zu "Unfällen" kommen – auch wenn die Blase selbst noch funktioniert.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-virus-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Medizinische Ursachen</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Blasenentzündungen, Diabetes, Nierenerkrankungen oder Prostataprobleme (bei Rüden) können ebenfalls Inkontinenz verursachen. Diese sollten immer tierärztlich abgeklärt werden.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="anzeichen" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              3. Typische Anzeichen erkennen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Inkontinenz zeigt sich auf verschiedene Weisen. Achten Sie auf diese Anzeichen:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-moon-line text-teal"></i>
                  Nächtliche Inkontinenz
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Nasse Stellen im Körbchen am Morgen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Feuchtes Fell im Bauch- oder Genitalbereich</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Uringeruch am Schlafplatz</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-sun-line text-teal"></i>
                  Inkontinenz tagsüber
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Tröpfeln beim Laufen oder Aufstehen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Kleine Pfützen, wo der Hund gelegen hat</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Häufigeres Lecken im Genitalbereich</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-sand rounded-xl p-6">
              <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal"></i>
                Inkontinenz oder Unsauberkeit?
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Der Unterschied: Bei echter Inkontinenz bemerkt Ihr Hund den Urinverlust oft gar nicht. Er liegt entspannt und unter ihm bildet sich eine Pfütze. Bei Unsauberkeit aus anderen Gründen (Stress, Angst, Protest) nimmt der Hund bewusst eine Haltung zum Urinieren ein.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section id="tierarzt" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              4. Wann zum Tierarzt?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Ein Tierarztbesuch ist bei Inkontinenz immer sinnvoll, um behandelbare Ursachen auszuschließen. <strong>Dringend</strong> sollten Sie zum Tierarzt, wenn:
            </p>

            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
                <i className="ri-alert-line text-orange-500"></i>
                Warnsignale – sofort zum Tierarzt
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span><strong>Blut im Urin</strong> – kann auf Infektionen oder ernstere Erkrankungen hinweisen</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span><strong>Schmerzen beim Urinieren</strong> – Winseln, Pressen, häufige Versuche</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span><strong>Plötzlich auftretende Inkontinenz</strong> – kann auf akute Erkrankungen hindeuten</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span><strong>Vermehrter Durst</strong> – mögliches Zeichen für Diabetes oder Nierenprobleme</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span><strong>Fieber oder Appetitlosigkeit</strong> – Hinweis auf Infektionen</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h4 className="font-semibold text-navy mb-4">Was der Tierarzt untersuchen wird:</h4>
              <div className="grid md:grid-cols-2 gap-4 text-gray-700">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Urinanalyse</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Blutuntersuchung</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Ultraschall der Blase</span>
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Neurologische Untersuchung</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Prostata-Check (bei Rüden)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal"></i>
                    <span>Hormonwerte (bei Hündinnen)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="management" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              5. Praktisches Management im Alltag
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Auch wenn Inkontinenz nicht immer heilbar ist, können Sie den Alltag für sich und Ihren Hund erheblich erleichtern:
            </p>

            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-teal"></i>
                  </div>
                  Häufigere Gassirunden
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Gehen Sie öfter, aber kürzer mit Ihrem Hund raus. Besonders wichtig:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Direkt nach dem Aufwachen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Nach jeder Mahlzeit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Kurz vor dem Schlafengehen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Eventuell auch nachts einmal</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-drop-line text-teal"></i>
                  </div>
                  Wassermanagement
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Wichtig:</strong> Niemals das Wasser komplett entziehen! Aber Sie können:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Die Hauptwassermenge auf den Tag verteilen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>2-3 Stunden vor dem Schlafengehen etwas weniger anbieten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Immer frisches Wasser verfügbar halten</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-home-heart-line text-teal"></i>
                  </div>
                  Schlafplatz-Schutz
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Schützen Sie Liegeplätze und Möbel:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Waschbare, wasserdichte Unterlagen verwenden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Mehrere Unterlagen bereithalten zum Wechseln</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Hundebetten mit abnehmbaren, waschbaren Bezügen wählen</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-sparkling-line text-teal"></i>
                  </div>
                  Richtige Reinigung
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Uringeruch vollständig entfernen ist wichtig:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>Enzymatische Reiniger</strong> verwenden – sie zersetzen Urin vollständig</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Normale Reiniger überdecken nur den Geruch</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Regelmäßig waschen bei 60°C, wenn möglich</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="hilfsmittel" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              6. Hilfreiche Produkte und Hilfsmittel
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Diese Produkte können den Alltag mit einem inkontinenten Hund erleichtern:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-layout-bottom-line text-teal text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Waschbare Unterlagen</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Wiederverwendbare, saugfähige Unterlagen mit wasserdichter Unterseite. Umweltfreundlicher und langfristig günstiger als Einwegprodukte.
                </p>
                <p className="text-sm text-teal font-medium">Ideal für: Schlafplätze, Sofa, Auto</p>
              </div>

              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-t-shirt-line text-teal text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Hygienehosen / Windeln</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Spezielle Hundehosen mit Einlagen. Gibt es als waschbare Variante oder mit Einweg-Einlagen. Wichtig: Richtige Größe wählen!
                </p>
                <p className="text-sm text-teal font-medium">Ideal für: Unterwegs, Besuche, nachts</p>
              </div>

              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-flask-line text-teal text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Enzymatische Reiniger</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Spezialreiniger, die Urin biologisch abbauen statt nur zu überdecken. Verhindert, dass der Hund dieselbe Stelle wieder markiert.
                </p>
                <p className="text-sm text-teal font-medium">Ideal für: Böden, Teppiche, Polster</p>
              </div>

              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-hotel-bed-line text-teal text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Wasserdichte Hundebetten</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Orthopädische Betten mit wasserdichtem Innenbezug und waschbarem Außenbezug. Schützt die Füllung und bleibt hygienisch.
                </p>
                <p className="text-sm text-teal font-medium">Ideal für: Hauptschlafplatz</p>
              </div>
            </div>

            <div className="bg-teal/10 rounded-xl p-6 mt-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-links-line text-teal text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-navy mb-2">Produktempfehlungen</h4>
                  <p className="text-gray-700">
                    Auf unserer Seite <Link to="/beduerfnisse/hygiene-inkontinenz" className="text-teal hover:underline font-medium cursor-pointer">Hygiene & Inkontinenz</Link> finden Sie konkrete Produktempfehlungen mit Erklärungen, warum wir diese empfehlen.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="wuerde" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              7. Die Würde Ihres Hundes bewahren
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Das Wichtigste zum Schluss: Inkontinenz ist für Ihren Hund genauso unangenehm wie für Sie. Viele Hunde schämen sich, wenn sie "Unfälle" haben – sie spüren, dass etwas nicht stimmt.
            </p>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-semibold text-navy mb-6">So unterstützen Sie Ihren Hund emotional:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-heart-line text-teal"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Niemals schimpfen oder bestrafen</h4>
                    <p className="text-gray-700">Ihr Hund kann nichts dafür. Bestrafung würde nur Angst und Stress verursachen – und das kann die Inkontinenz sogar verschlimmern.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-emotion-happy-line text-teal"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Ruhig und gelassen bleiben</h4>
                    <p className="text-gray-700">Wenn ein Malheur passiert, räumen Sie es kommentarlos auf. Ihr Hund nimmt Ihre Stimmung wahr.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-sparkling-2-line text-teal"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Für Sauberkeit sorgen</h4>
                    <p className="text-gray-700">Halten Sie das Fell im Genitalbereich sauber und trocken, um Hautreizungen zu vermeiden.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-hand-heart-line text-teal"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Routine beibehalten</h4>
                    <p className="text-gray-700">Geben Sie Ihrem Hund Sicherheit durch gewohnte Abläufe und viel Zuneigung.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-sand rounded-xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed italic">
                "Inkontinenz ist eine der Herausforderungen des Älterwerdens – für Mensch und Tier. Mit Geduld, den richtigen Hilfsmitteln und viel Liebe können Sie Ihrem Seniorhund trotzdem ein würdevolles, glückliches Leben ermöglichen."
              </p>
            </div>
          </section>

          {/* Checklist */}
          <section className="bg-white rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-3">
              <i className="ri-checkbox-multiple-line text-teal"></i>
              Checkliste: Inkontinenz-Management
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-navy mb-3">Sofort umsetzen:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Tierarzttermin vereinbaren</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Waschbare Unterlagen besorgen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Gassirunden-Rhythmus anpassen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Enzymatischen Reiniger kaufen</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-navy mb-3">Langfristig beachten:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Regelmäßige Tierarzt-Kontrollen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Hautpflege im Genitalbereich</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Geduld und Verständnis zeigen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Routine beibehalten</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-2xl font-bold text-navy mb-8">
              Weitere hilfreiche Artikel
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.link}
                  className="group bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-navy mb-2 group-hover:text-teal transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
